import { useParams, Link, useNavigate } from "react-router-dom";
import { SpaceRow } from "../lib/api";
import { useSpaceContext } from "./SpaceLayout";

export default function SpacePage() {
  const { username, spaceSlug } = useParams<{ username: string; spaceSlug: string }>();
  const navigate = useNavigate();
  const { pages, space: ctxSpace, isOwner } = useSpaceContext();

  // Use space from context (already fetched by SpaceLayout)
  const space: SpaceRow | null = ctxSpace;

  return (
    <main className="flex-1 overflow-y-auto">
      <div className="max-w-3xl mx-auto px-8 py-10 space-y-8">

        {/* Space header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{space?.name ?? spaceSlug}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">@{username}/{spaceSlug}</p>
            {space?.description && (
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">{space.description}</p>
            )}
          </div>
          {isOwner && (
            <Link
              to={`/${username}/${spaceSlug}/settings`}
              className="shrink-0 ml-4 px-3 py-1.5 text-xs font-medium border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600 hover:text-gray-900 dark:hover:text-gray-100 rounded-lg transition-colors flex items-center gap-1.5"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Settings
            </Link>
          )}
        </div>

        {/* Empty state */}
        {pages.length === 0 && (
          <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-xl p-8 text-center">
            <p className="text-2xl mb-3">📭</p>
            <h2 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">This space has no pages yet</h2>
            {isOwner ? (
              <>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
                  Create your first page manually, or connect an agent to generate content.
                </p>
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => navigate(`/${username}/${spaceSlug}/new`)}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    New Page
                  </button>
                  <Link
                    to={`/${username}/${spaceSlug}/settings`}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:border-gray-400 dark:hover:border-gray-600 transition-colors"
                  >
                    Connect an Agent →
                  </Link>
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Come back later — content is on its way.
              </p>
            )}
          </div>
        )}

      </div>
    </main>
  );
}
